import { ComplianceFile, DocumentLink, DocumentOrigin, PolicyDocumentKind, ProtoDb, UnusualReport } from '../models/domain.models';
import { hashOf } from './commission-math';
import { emissionOperations } from './compliance-engine';
import { addDays } from './rng';
import { MEMBER, MEMBER_FED, MEMBERSHIPS } from './seed-named';

const ACTIVITIES = ['Comerciante minorista', 'Profesional independiente', 'Empleado en relación de dependencia', 'Productor agropecuario', 'Transportista', 'Docente', 'Jubilado'];
const FUNDS = ['Ingresos por su actividad', 'Sueldo y ahorros', 'Honorarios profesionales', 'Venta de un inmueble', 'Jubilación y ahorros'];
const UNUSUAL_TEXTS = [
  'Pago de la prima anual en efectivo por un monto muy superior al habitual del cliente.',
  'Pedido de anulación inmediata tras la emisión, con devolución a una cuenta de un tercero.',
  'El cliente cambia el medio de pago y el domicilio en tres oportunidades en un mes.',
  'Contratación de varias pólizas de vida con capitales altos sin justificación de ingresos.',
];

function pad(n: number, size: number): string {
  return String(n).padStart(size, '0');
}

function plateOf(subject: string): string | null {
  return /\(([^)]+)\)$/.exec(subject)?.[1] ?? null;
}

/** P-70 / P-23 / P-87 / P-86 seed, derived from the joined database without the RNG, so no tree's generated data moves. */
export function enrichCompliance(db: ProtoDb, today: string): void {
  const upTo = (date: string) => (date > today ? today : date);
  let n = 0;
  const add = (
    name: string, kind: PolicyDocumentKind, origin: DocumentOrigin, date: string, ownerOid: string, personOid: string,
    links: DocumentLink[], uploadedByOid: string | null = null,
  ) => {
    db.documents.push({
      oid: `doc-c-${pad(++n, 4)}`, name, kind, origin, date: upTo(date), ownerOid, personOid, policyOid: null, claimOid: null,
      uploadedByOid, links,
    });
  };

  for (const quote of db.quotes.filter(q => ['presented', 'chosen-by-client', 'accepted', 'issued'].includes(q.status))) {
    add(`Comparativo ${quote.number}.pdf`, 'comparative', 'generated', quote.share?.createDate ?? quote.createDate, quote.ownerOid, quote.personOid, []);
  }
  db.persons.forEach((person, i) => {
    if (person.kind === 'client' && i % 4 === 1) {
      add(`Documento de identidad - ${person.displayName}.pdf`, 'identity', 'client-supplied', person.createDate, person.ownerOid, person.oid, []);
    }
  });
  for (const policy of db.policies.filter(p => p.lineOfBusiness === 'auto' && p.status === 'active')) {
    const h = hashOf(policy.oid);
    const plate = plateOf(policy.subject);
    if (h % 7 === 2 && plate) {
      add(`Cédula ${plate}.pdf`, 'vehicle-card', 'uploaded', policy.startDate, policy.ownerOid, policy.personOid,
        [{ type: 'policy', oid: policy.oid }], policy.ownerOid);
    } else if (policy.origin === 'platform' && h % 11 === 0) {
      add(`Inspección previa ${policy.number}.pdf`, 'inspection', 'from-company', policy.startDate, policy.ownerOid, policy.personOid,
        [{ type: 'policy', oid: policy.oid }]);
    }
  }
  db.claims.forEach((claim, i) => {
    const claimLink: DocumentLink = { type: 'claim', oid: claim.oid };
    if (claim.policeReport) {
      add(`Denuncia policial ${claim.number}.pdf`, 'police-report', 'client-supplied', claim.reportedDate, claim.ownerOid, claim.personOid,
        [claimLink, { type: 'policy', oid: claim.policyOid }]);
    }
    if (/Choque|Robo parcial|Granizo/.test(claim.type) && i % 2 === 0) {
      const ticket = db.tickets.find(t => t.relatedType === 'claim' && t.relatedOid === claim.oid);
      add(`Presupuesto del taller ${claim.number}.pdf`, 'budget', 'uploaded', claim.reportedDate, claim.ownerOid, claim.personOid,
        ticket ? [claimLink, { type: 'ticket', oid: ticket.oid }] : [claimLink], claim.ownerOid);
    }
    if (/Choque|Granizo|Incendio|Daños/.test(claim.type) && i % 3 === 0) {
      add(`Fotos del siniestro ${claim.number}.jpg`, 'photo', 'client-supplied', claim.reportedDate, claim.ownerOid, claim.personOid, [claimLink]);
    }
  });

  const files: ComplianceFile[] = [];
  for (const person of db.persons.filter(p => p.kind === 'client')) {
    const h = hashOf(person.oid);
    if (h % 3 === 2) continue;
    const complete = h % 3 === 0;
    const pep = h % 13 === 0;
    files.push({
      personOid: person.oid, ownerOid: person.ownerOid, state: complete ? 'complete' : 'incomplete',
      updateDate: addDays(today, -(h % 200)), activity: ACTIVITIES[h % ACTIVITIES.length],
      fundsOrigin: complete ? FUNDS[h % FUNDS.length] : '', pep, pepRole: pep ? 'Funcionario público provincial' : '',
      obligedSubjectDeclared: complete, attachments: complete ? [`Declaración de origen de fondos - ${person.displayName}.pdf`] : [],
    });
  }
  db.complianceFiles = files;

  const reportFor = (ownerOid: string, memberOids: string[], pick: number, fields: Partial<UnusualReport> & Pick<UnusualReport, 'status' | 'operationDate'>, serial: number) => {
    const clients = db.persons.filter(p => memberOids.includes(p.ownerOid) && db.policies.some(x => x.personOid === p.oid))
      .sort((a, b) => a.oid.localeCompare(b.oid));
    const person = clients[pick % Math.max(clients.length, 1)];
    if (!person) return;
    const policies = db.policies.filter(p => p.personOid === person.oid).slice(0, 2);
    db.unusualReports.push({
      oid: `unu-${pad(serial, 4)}`, ownerOid, personOid: person.oid, policyOids: policies.map(p => p.oid),
      description: UNUSUAL_TEXTS[serial % UNUSUAL_TEXTS.length], attachments: serial % 2 === 0 ? ['Comprobante de pago en efectivo.pdf'] : [],
      createDate: addDays(fields.operationDate, 1), updateDate: addDays(fields.operationDate, 1), filedDate: null, receiptNumber: null,
      ...fields,
    });
  };
  const rios = [MEMBER.martin];
  reportFor(MEMBER.martin, rios, 2, {
    status: 'filed', operationDate: addDays(today, -45), updateDate: addDays(today, -30), filedDate: addDays(today, -30),
    receiptNumber: `CON-${today.slice(0, 4)}-0412`,
  }, 1);
  reportFor(MEMBER.martin, rios, 7, { status: 'prepared', operationDate: addDays(today, -12), updateDate: addDays(today, -8) }, 2);
  reportFor(MEMBER.martin, rios, 11, { status: 'draft', operationDate: addDays(today, -3) }, 3);
  reportFor(MEMBER_FED.martin, [MEMBER_FED.martin, MEMBER_FED.pablo], 1, { status: 'draft', operationDate: addDays(today, -6) }, 4);

  const year = Number(today.slice(0, 4));
  const from = `${year - 1}-01-01`;
  const to = `${year - 1}-12-31`;
  const rows = emissionOperations(db, new Set(MEMBERSHIPS['prod'].map(m => m.memberOid)), from, to);
  db.emissionBooks = [{
    oid: 'book-0001', username: 'prod', from, to, generatedDate: addDays(today, -120), count: rows.length,
    premiumTotal: rows.reduce((sum, r) => sum + r.premium, 0), format: 'PDF',
  }];
}
